import { DeleteIcon } from '@chakra-ui/icons';
import { Box, Button, Card, CardBody, Center, Flex, Grid, GridItem, Heading, Image, Spacer, Table, TableContainer, Tbody, Td, Text, Tr, VStack, useColorModeValue } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, useNavigate } from 'react-router-dom';
import ModalLoading from '../../components/Loader/ModalLoader.jsx';
import { isEmptyObject } from '../../helper/utils/isEmptyObject.js';
import { urlNavigate } from '../../helper/utils/navigation.js';
import { convertDateFormat } from '../../helper/utils/utils.date.js';
import getStaticAsset from '../../helper/utils/utilsStaticAsset';
import { doAddMinor, doDeleteMinor } from '../../redux/waiver/waiver.actions.js';
import AddMinor from './AddMinor.jsx';

export default function FormChildren() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const bgColor = useColorModeValue('white', 'gray.800');
    const page = JSON.parse(localStorage.getItem('page') || '{}');

    const { data = {}, email = '', minor = [], loadingSubmission = false } = useSelector((s) => s?.waiver);

    const isContainNewMinor = () => minor.some((min) => min.isNew);

    const handleMinor = (val) => {
        const a = [...minor, { ...val, isNew: true }].map((el, idx) => ({ ...el, key: idx }));
        dispatch(doAddMinor(a));
    };

    const handleDelete = (el) => {
        const filter = minor.filter((ch) => ch?.key !== el?.key);
        dispatch(doDeleteMinor(filter));
    };

    const handleSubmit = () => {
        const minors = minor.map((el) => ({ ...el, parentId: data?.phone ?? '' }));

        const payload = {
            email: email === '' ? localStorage.getItem('email') : email,
            telp: data?.phone ?? '',
            birthDate: data?.birthDate ?? '',
            signature: '',
            fullName: data?.fullname ?? '',
            gender: data?.gender ?? '',
            minor: minors,
        };

        localStorage.setItem('payload', JSON.stringify(payload));
        localStorage.setItem('minor', JSON.stringify(minors));

        // Navigate to agreement page
        navigate(urlNavigate('waiver', 'agreement'));
    };

    const handleBack = () => {
        navigate(urlNavigate('waiver', 'user'));
    };

    if (isEmptyObject(data)) {
        return <Navigate to={urlNavigate('waiver', 'user')} />;
    }

    return (
        <Box
            minH='100vh'
            w='full'
            bgImage={getStaticAsset(page['home']['image'])}
            bgSize='cover'
            bgPosition='center'
            bgRepeat='no-repeat'
            position='relative'
            overflow='hidden'
            _before={{
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                bg: 'rgba(0, 0, 0, 0.5)',
                zIndex: 0,
            }}>
            <ModalLoading loading={loadingSubmission} />

            <Center minH='100vh' position='relative' zIndex={1} p={4} py={8}>
                <VStack spacing={4} w='full' maxW='1200px'>
                    <Box w='full' bg={bgColor} overflow='hidden' rounded='2xl' boxShadow='2xl' p={{ base: 6, md: 8 }} backdropFilter='blur(10px)'>
                        {/* Logo */}
                        <Center mb={6}>
                            <Image src={getStaticAsset('dream_us_logo.webp')} alt='Dream Us Logo' w={{ base: '60%', md: '30%' }} maxW='200px' />
                        </Center>

                        {/* Main Content */}
                        <Grid templateColumns={{ base: '1fr', lg: '1fr 2fr' }} gap={6}>
                            {/* Left Side - Personal Data Review */}
                            <GridItem>
                                <VStack spacing={4} align='stretch'>
                                    <Heading fontSize={{ base: 'xl', md: '2xl' }} fontWeight={600}>
                                        {t('personal_data')}
                                    </Heading>

                                    <Card variant='outline' bg='gray.50'>
                                        <CardBody>
                                            <TableContainer>
                                                <Table variant='unstyled' size='sm'>
                                                    <Tbody>
                                                        <Tr>
                                                            <Td fontSize='sm' color='gray.600' maxW={{ base: '10px', md: 'full' }}>
                                                                {t('full_name')}
                                                            </Td>
                                                            <Td textAlign='right' fontWeight={600}>
                                                                {data?.fullname ?? ''}
                                                            </Td>
                                                        </Tr>
                                                        <Tr>
                                                            <Td fontSize='sm' color='gray.600' maxW={{ base: '10px', md: 'full' }}>
                                                                {t('email')}
                                                            </Td>
                                                            <Td textAlign='right' fontWeight={600}>
                                                                {email || localStorage.getItem('email')}
                                                            </Td>
                                                        </Tr>
                                                        <Tr>
                                                            <Td fontSize='sm' color='gray.600' maxW={{ base: '10px', md: 'full' }}>
                                                                {t('telp')}
                                                            </Td>
                                                            <Td textAlign='right' fontWeight={600}>
                                                                {data?.phone ?? ''}
                                                            </Td>
                                                        </Tr>
                                                        <Tr>
                                                            <Td fontSize='sm' color='gray.600' maxW={{ base: '10px', md: 'full' }}>
                                                                {t('birth_date')}
                                                            </Td>
                                                            <Td textAlign='right' fontWeight={600}>
                                                                {convertDateFormat(data?.birthDate ?? '')}
                                                            </Td>
                                                        </Tr>
                                                        <Tr>
                                                            <Td fontSize='sm' color='gray.600' maxW={{ base: '10px', md: 'full' }}>
                                                                Gender
                                                            </Td>
                                                            <Td textAlign='right' fontWeight={600} textTransform='capitalize'>
                                                                {data?.gender ?? ''}
                                                            </Td>
                                                        </Tr>
                                                    </Tbody>
                                                </Table>
                                            </TableContainer>
                                        </CardBody>
                                    </Card>

                                    <Button w='full' variant='outline' colorScheme='primary' size='md' onClick={() => navigate(urlNavigate('waiver', 'user?isEdit=true'))}>
                                        {t('edit')} Personal Data
                                    </Button>
                                </VStack>
                            </GridItem>

                            {/* Right Side - Children Form */}
                            <GridItem>
                                <VStack spacing={4} align='stretch'>
                                    <Heading fontSize={{ base: 'xl', md: '2xl' }} fontWeight={600}>
                                        {t('any_under_18')}
                                    </Heading>

                                    {/* Children List */}
                                    <Box
                                        maxH='400px'
                                        overflowY='auto'
                                        css={{
                                            '&::-webkit-scrollbar': {
                                                width: '8px',
                                            },
                                            '&::-webkit-scrollbar-track': {
                                                background: '#f1f1f1',
                                                borderRadius: '10px',
                                            },
                                            '&::-webkit-scrollbar-thumb': {
                                                background: '#888',
                                                borderRadius: '10px',
                                            },
                                            '&::-webkit-scrollbar-thumb:hover': {
                                                background: '#555',
                                            },
                                        }}>
                                        {minor.length === 0 ? (
                                            <Card variant='outline' bg='gray.50'>
                                                <CardBody>
                                                    <Text textAlign='center' color='gray.500' fontSize='sm'>
                                                        No children added yet. Click the button below to add.
                                                    </Text>
                                                </CardBody>
                                            </Card>
                                        ) : (
                                            <VStack spacing={3}>
                                                {minor.map((el) => (
                                                    <Card key={el?.key} w='full' variant='outline' bg='gray.50'>
                                                        <CardBody>
                                                            <TableContainer>
                                                                <Table variant='unstyled' size='sm'>
                                                                    <Tbody>
                                                                        <Tr>
                                                                            <Td fontSize='sm' color='gray.600' maxW={{ base: '10px', md: 'full' }}>
                                                                                {t('full_name')}
                                                                            </Td>
                                                                            <Td textAlign='right' fontWeight={600}>
                                                                                {el?.name ?? ''}
                                                                            </Td>
                                                                        </Tr>
                                                                        <Tr>
                                                                            <Td fontSize='sm' color='gray.600' maxW={{ base: '10px', md: 'full' }}>
                                                                                {t('birth_date')}
                                                                            </Td>
                                                                            <Td textAlign='right' fontWeight={600}>
                                                                                {convertDateFormat(el?.birthDate ?? '')}
                                                                            </Td>
                                                                        </Tr>
                                                                        <Tr>
                                                                            <Td fontSize='sm' color='gray.600' maxW={{ base: '10px', md: 'full' }}>
                                                                                Gender
                                                                            </Td>
                                                                            <Td textAlign='right' fontWeight={600} textTransform='capitalize'>
                                                                                {el?.gender ?? ''}
                                                                            </Td>
                                                                        </Tr>
                                                                    </Tbody>
                                                                </Table>
                                                            </TableContainer>
                                                            <Spacer />
                                                            {el?.isNew && (
                                                                <Flex w='full' justifyContent='end' mt={2}>
                                                                    <Button size='sm' colorScheme='red' variant='ghost' leftIcon={<DeleteIcon />} onClick={() => handleDelete(el)}>
                                                                        {t('remove')}
                                                                    </Button>
                                                                </Flex>
                                                            )}
                                                        </CardBody>
                                                    </Card>
                                                ))}
                                            </VStack>
                                        )}
                                    </Box>

                                    {/* Add Children Button */}
                                    <AddMinor handleMinor={handleMinor} />
                                </VStack>
                            </GridItem>
                        </Grid>
                    </Box>

                    {/* Action Buttons*/}
                    <Box w='full' bg={bgColor} rounded='2xl' boxShadow='2xl' p={{ base: 4, md: 6 }} backdropFilter='blur(10px)'>
                        <Flex gap={4} direction={{ base: 'column', md: 'row' }}>
                            <Button w='full' size='lg' fontSize='md' variant='outline' colorScheme='gray' onClick={handleBack}>
                                {t('previous')}
                            </Button>
                            <Button w='full' size='lg' fontSize='md' colorScheme='primary' onClick={handleSubmit}>
                                {isContainNewMinor() ? 'Continue to Agreement' : t('next')}
                            </Button>
                        </Flex>
                    </Box>
                </VStack>
            </Center>
        </Box>
    );
}
